import { useAuth0 } from '@auth0/auth0-react';
import { BarChart3, ChevronRight, EyeOff, FlaskConical, Layers, Users } from 'lucide-react';
import * as React from 'react';

import { hasActiveMaintenance, UpcomingMaintenanceCard } from '@/components/MaintenanceWarning/MaintenanceWarning';
import { MiniDonut } from '@/components/ScenarioExplorer/SuiteHelpers';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import WorkspaceTable from '@/components/WorkspaceTable/WorkspaceTable';
import { isDemoModeAllowed } from '@/constants';
import { getWorkspaceUsage, listOrgMembers, queryScenarios, querySuites } from '@/lib/api/rome';
import { useOrgClaims } from '@/lib/hooks/useOrgClaims';
import { useOrgNavigate } from '@/lib/hooks/useOrgNavigate';
import type { DailyUsage, OrgMember, ScenarioRun, SuiteSummary } from '@/lib/types/rome';
import { Tabs } from '@/lib/types/tabs';
import { cn } from '@/lib/utils/cn';
import { getTimeAgo } from '@/lib/utils/date';
import { demoFiltersToQueryParams } from '@/lib/utils/demoFilters';
import { notifyDemoHidden } from '@/lib/utils/demoHideToast';
import { getOutcomeConfig, getUserDisplay } from '@/lib/utils/scenarios';
import { useDemoMode, useNavigation, useWorkspace } from '@/providers';

function getTimeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function passRateColor(rate: number): string {
  if (rate >= 80) return 'text-teal-600 dark:text-teal-400';
  if (rate >= 60) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

function RecentScenariosCard() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useOrgNavigate();
  const { orgId } = useOrgClaims();
  const { enabled: demoEnabled, filters: demoFilters, updateFilters: updateDemoFilters } = useDemoMode();
  const isDemoActive = demoEnabled && isDemoModeAllowed(orgId);
  const [runs, setRuns] = React.useState<ScenarioRun[]>([]);
  const [loading, setLoading] = React.useState(true);

  const hideScenario = (name: string) => {
    if (!name || demoFilters.hiddenScenarios.includes(name)) return;
    updateDemoFilters({ hiddenScenarios: [...demoFilters.hiddenScenarios, name] });
    notifyDemoHidden();
  };

  React.useEffect(() => {
    let mounted = true;
    async function fetchRuns() {
      try {
        const token = await getAccessTokenSilently();
        const filters: Parameters<typeof queryScenarios>[1] = { limit: 5 };
        if (isDemoActive) Object.assign(filters, demoFiltersToQueryParams(demoFilters));
        const response = await queryScenarios(token, filters);
        if (mounted && response.status === 200 && response.data?.runs) {
          setRuns(response.data.runs);
        }
      } catch (err) {
        console.error('[HomePage] Error fetching recent runs:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchRuns();
    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently, isDemoActive, demoFilters]);

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center justify-between border-gray-100 border-b px-4 py-3 dark:border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300">
            <FlaskConical className="size-3.5" />
          </div>
          <h3 className="font-medium text-gray-800 text-sm dark:text-white">Recent Scenarios</h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/scenarios')}
          className="flex cursor-pointer items-center gap-0.5 text-gray-500 text-xs hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          View all
          <ChevronRight className="size-3" />
        </button>
      </div>

      <div className="flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="size-5" className="text-gray-600 dark:text-gray-300" />
          </div>
        ) : runs.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-xs dark:text-gray-400">No recent scenarios</div>
        ) : (
          <div className="flex flex-col divide-y divide-gray-100 dark:divide-white/5">
            {runs.map((run) => {
              const outcome = getOutcomeConfig(run.outcome);
              const Icon = outcome.icon;
              return (
                <div key={run.run_id} className="group relative flex items-center">
                  <button
                    type="button"
                    onClick={() => navigate(`/scenarios?run=${encodeURIComponent(run.run_id)}`)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 px-4 py-3 pr-9 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                  >
                    <Icon className={cn('size-4 shrink-0', outcome.color)} />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="min-w-0 truncate font-medium text-[13px] text-gray-900 dark:text-gray-100">
                        {run.scenario_name || '(Unnamed)'}
                      </span>
                      <div className="truncate text-[12px]">
                        <span className={outcome.color}>
                          {outcome.label}
                          {getTimeAgo(run.completed_at || run.started_at)
                            ? ` ${getTimeAgo(run.completed_at || run.started_at)}`
                            : ''}
                        </span>
                        {getUserDisplay(run) !== '-' && (
                          <>
                            <span className="mx-1.5 text-gray-300 dark:text-gray-600">&bull;</span>
                            <span className="text-gray-500 dark:text-gray-400">{getUserDisplay(run)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                  {isDemoActive && run.scenario_name ? (
                    <SimpleTooltip content="Hide this scenario from the demo view">
                      <button
                        type="button"
                        aria-label={`Hide scenario ${run.scenario_name}`}
                        onClick={() => hideScenario(run.scenario_name)}
                        className="absolute right-2 flex size-6 cursor-pointer items-center justify-center rounded-md text-gray-400 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
                      >
                        <EyeOff className="size-3.5" />
                      </button>
                    </SimpleTooltip>
                  ) : (
                    <ChevronRight className="absolute right-3 size-3.5 text-gray-300 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-gray-600" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RecentSuitesCard() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useOrgNavigate();
  const { orgId } = useOrgClaims();
  const { enabled: demoEnabled, filters: demoFilters } = useDemoMode();
  const isDemoActive = demoEnabled && isDemoModeAllowed(orgId);
  const [suites, setSuites] = React.useState<SuiteSummary[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    async function fetchSuites() {
      try {
        const token = await getAccessTokenSilently();
        const filters: Parameters<typeof querySuites>[1] = { limit: 5 };
        if (isDemoActive) Object.assign(filters, demoFiltersToQueryParams(demoFilters));
        const response = await querySuites(token, filters);
        if (mounted && response.status === 200 && response.data?.suites) {
          setSuites(response.data.suites);
        }
      } catch (err) {
        console.error('[HomePage] Error fetching recent suites:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchSuites();
    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently, isDemoActive, demoFilters]);

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center justify-between border-gray-100 border-b px-4 py-3 dark:border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300">
            <Layers className="size-3.5" />
          </div>
          <h3 className="font-medium text-gray-800 text-sm dark:text-white">Recent Suites</h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('/scenarios?view=suites')}
          className="flex cursor-pointer items-center gap-0.5 text-gray-500 text-xs hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          View all
          <ChevronRight className="size-3" />
        </button>
      </div>

      <div className="flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="size-5" className="text-gray-600 dark:text-gray-300" />
          </div>
        ) : suites.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-xs dark:text-gray-400">No scenario suites</div>
        ) : (
          <div className="flex flex-col divide-y divide-gray-100 dark:divide-white/5">
            {suites.map((suite) => (
              <button
                key={suite.suite_tag}
                type="button"
                onClick={() => navigate(`/scenarios?view=suites&suite=${encodeURIComponent(suite.suite_tag)}`)}
                className="group flex cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
              >
                <MiniDonut passed={Math.round(suite.latest_pass_rate)} total={100} size={18} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="min-w-0 truncate font-medium text-[13px] text-gray-900 dark:text-gray-100">
                    {suite.name}
                  </span>
                  <div className="truncate text-[12px]">
                    <span className={passRateColor(suite.latest_pass_rate)}>{suite.latest_pass_rate}% pass rate</span>
                    {suite.last_suite_run_at && getTimeAgo(suite.last_suite_run_at) && (
                      <>
                        <span className="mx-1.5 text-gray-300 dark:text-gray-600">&bull;</span>
                        <span className="text-gray-500 dark:text-gray-400">{getTimeAgo(suite.last_suite_run_at)}</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="size-3.5 shrink-0 text-gray-300 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-gray-600" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniBarChart({ data }: { data: DailyUsage[] }) {
  const maxVwh = Math.max(...data.map((d) => d.vwh), 0.01);

  return (
    <div className="flex h-12 items-end gap-[2px]">
      {data.map((day) => {
        const heightPct = Math.max((day.vwh / maxVwh) * 100, 4);
        const dayLabel = new Date(`${day.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'narrow' });
        return (
          <div key={day.date} className="group relative flex flex-1 flex-col items-center" style={{ height: 48 }}>
            <div className="flex w-full flex-1 items-end">
              <div
                className="w-full rounded-sm bg-sky-400/70 transition-colors group-hover:bg-sky-500 dark:bg-sky-500/50 dark:group-hover:bg-sky-400/70"
                style={{ height: `${heightPct}%`, minHeight: 2 }}
              />
            </div>
            <span className="mt-1 text-[8px] text-gray-400 dark:text-gray-500">{dayLabel}</span>
            <div className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-900 px-1.5 py-0.5 text-[9px] text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-100 dark:text-gray-900">
              {day.vwh.toFixed(1)}h
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UsageCard() {
  const { getAccessTokenSilently } = useAuth0();
  const { switchTab } = useNavigation();
  const [totalVwh, setTotalVwh] = React.useState<number | null>(null);
  const [dailyData, setDailyData] = React.useState<DailyUsage[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    async function fetchUsage() {
      try {
        const token = await getAccessTokenSilently();
        const now = new Date();
        const fourteenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13);
        const startTime = Math.floor(fourteenDaysAgo.getTime() / 1000);
        const endTime = Math.floor(now.getTime() / 1000);

        const response = await getWorkspaceUsage(token, startTime, endTime);
        if (mounted && response.status === 200 && response.data?.data) {
          const breakdown = response.data.data.daily_breakdown || [];
          const todayStr = now.toISOString().split('T')[0];
          const todayEntry = breakdown.find((d) => d.date === todayStr);
          setTotalVwh(todayEntry?.vwh ?? 0);
          setDailyData(breakdown.slice(-14));
        }
      } catch {
        // Gracefully handle
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchUsage();
    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center justify-between border-gray-100 border-b px-4 py-3 dark:border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300">
            <BarChart3 className="size-3.5" />
          </div>
          <h3 className="font-medium text-gray-800 text-sm dark:text-white">Usage</h3>
        </div>
        <button
          type="button"
          onClick={() => switchTab(Tabs.USAGE)}
          className="flex cursor-pointer items-center gap-0.5 text-gray-500 text-xs hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          View all
          <ChevronRight className="size-3" />
        </button>
      </div>

      <div className="px-4 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner size="size-4" className="text-gray-600 dark:text-gray-300" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold text-gray-900 text-xl tabular-nums dark:text-white">
                {totalVwh !== null ? totalVwh.toFixed(1) : '—'}
              </span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400">VWH today</span>
            </div>
            {dailyData.length > 0 && <MiniBarChart data={dailyData} />}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberAvatar({ picture, name, initials }: { picture: string | null; name: string | null; initials: string }) {
  const [failed, setFailed] = React.useState(false);

  if (!picture || failed) {
    return (
      <div className="flex size-8 items-center justify-center rounded-full border-2 border-white bg-gray-100 font-medium text-[10px] text-gray-600 dark:border-gray-900 dark:bg-white/10 dark:text-gray-300">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={picture}
      alt={name || ''}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="size-8 rounded-full border-2 border-white object-cover dark:border-gray-900"
    />
  );
}

function TeamCard() {
  const { getAccessTokenSilently } = useAuth0();
  const { switchTab } = useNavigation();
  const { orgName } = useOrgClaims();
  const [members, setMembers] = React.useState<OrgMember[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    async function fetchMembers() {
      try {
        const token = await getAccessTokenSilently();
        const response = await listOrgMembers(token);
        if (mounted && response.status === 200 && response.data) {
          setMembers(response.data.data || []);
        }
      } catch {
        // Gracefully handle
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchMembers();
    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  const teamMembers = members.filter((m) => m.role === 'team_admin' || m.role === 'team_member' || !m.role);

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center justify-between border-gray-100 border-b px-4 py-3 dark:border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="flex size-7 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300">
            <Users className="size-3.5" />
          </div>
          <h3 className="font-medium text-gray-800 text-sm dark:text-white">Team</h3>
        </div>
        <button
          type="button"
          onClick={() => switchTab(Tabs.TEAM)}
          className="flex cursor-pointer items-center gap-0.5 text-gray-500 text-xs hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Manage
          <ChevronRight className="size-3" />
        </button>
      </div>

      <div className="px-4 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner size="size-4" className="text-gray-600 dark:text-gray-300" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800 text-sm dark:text-gray-100">
                  {orgName || 'Your Organization'}
                </div>
                <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                  {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                </div>
              </div>
              {teamMembers.length > 0 && (
                <div className="flex items-center">
                  {teamMembers.slice(0, 5).map((m, i) => {
                    const initials = (m.name || m.email || '?')
                      .split(/[\s@]+/)
                      .slice(0, 2)
                      .map((s) => s[0]?.toUpperCase())
                      .join('');
                    return (
                      <div key={m.user_id} style={{ marginLeft: i === 0 ? 0 : -10 }}>
                        <MemberAvatar picture={m.picture} name={m.name} initials={initials} />
                      </div>
                    );
                  })}
                  {teamMembers.length > 5 && (
                    <div
                      style={{ marginLeft: -10 }}
                      className="flex size-8 items-center justify-center rounded-full border-2 border-white bg-gray-100 font-medium text-[10px] text-gray-500 dark:border-gray-900 dark:bg-white/10 dark:text-gray-400"
                    >
                      +{teamMembers.length - 5}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => switchTab(Tabs.TEAM)}
              className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border border-gray-200 py-1.5 text-gray-600 text-xs transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:border-white/10 dark:text-gray-400 dark:hover:border-white/20 dark:hover:bg-white/5 dark:hover:text-gray-200"
            >
              Invite teammates
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Landing page with workspaces, recent scenarios/suites, usage, and team info.
 */
export default function HomePage() {
  const { user } = useAuth0();
  const { switchTab } = useNavigation();
  const {
    workspaces,
    workspacesFetched,
    connectedWorkspace,
    isStartingWorkspace,
    restartingWorkspaceId,
    availableVersions,
    availableImages,
    orgImagesError,
    versionsLoading,
    progressMap,
    fetchProgressForWorkspace,
    localWorkspace,
    localWorkspaceId,
    isLocalDev,
    viewWorkspace,
    disconnect,
    startWorkspace,
    restartWorkspace,
    stopWorkspace,
    terminateWorkspace,
  } = useWorkspace();

  const handleViewWorkspace = React.useCallback(
    (workspace: Parameters<typeof viewWorkspace>[0]) => {
      viewWorkspace(workspace);
      switchTab(Tabs.WORKSPACE);
    },
    [viewWorkspace, switchTab]
  );

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      {/* Header */}
      <header className="mb-5">
        <h1 className="font-semibold text-gray-900 text-xl tracking-tight dark:text-white">
          Good {getTimeOfDay()},{' '}
          <span className="text-gray-500 dark:text-gray-400">
            {user?.given_name || user?.name?.split(' ')[0] || 'there'}
          </span>
        </h1>
        <p className="mt-1 text-gray-500 text-sm dark:text-gray-400">
          Here is what's happening across your workspaces and scenarios.
        </p>
      </header>

      {/* Main Content */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-3">
        {/* Left column (2 cols): workspaces + recent scenarios/suites */}
        <div className="flex flex-col gap-3 lg:col-span-2">
          <WorkspaceTable
            workspaces={isLocalDev && localWorkspace ? [localWorkspace, ...workspaces] : workspaces}
            onViewWorkspace={handleViewWorkspace}
            title="Workspaces"
            onStartWorkspace={startWorkspace}
            onRestartWorkspace={restartWorkspace}
            onStopWorkspace={stopWorkspace}
            onTerminateWorkspace={terminateWorkspace}
            onDisconnect={connectedWorkspace ? disconnect : undefined}
            isStarting={isStartingWorkspace}
            isLoading={!workspacesFetched}
            availableVersions={availableVersions}
            availableImages={availableImages}
            orgImagesError={orgImagesError}
            versionsLoading={versionsLoading}
            readOnlyWorkspaceIds={isLocalDev && localWorkspace ? new Set([localWorkspaceId]) : undefined}
            connectedWorkspaceId={connectedWorkspace?.id}
            restartingWorkspaceId={restartingWorkspaceId}
            progressMap={progressMap}
            fetchProgressForWorkspace={fetchProgressForWorkspace}
            maxRows={5}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <RecentScenariosCard />
            <RecentSuitesCard />
          </div>
        </div>

        {/* Right sidebar (1 col): upcoming maintenance (conditional) + team + usage */}
        <div className="flex flex-col gap-3 lg:col-span-1">
          <UpcomingMaintenanceCard
            workspaces={workspaces
              .filter(hasActiveMaintenance)
              .map((w) => ({ id: w.id, name: w.name, scheduledShutdownAt: w.scheduledShutdownAt as number }))}
          />
          <TeamCard />
          <UsageCard />
        </div>
      </div>
    </div>
  );
}
